import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { AppDialog } from '@/components/ui/AppDialog';
import app from '@/services/firebaseService';

// 구독 결제는 모바일 앱(App Store / Google Play)에서만 한다. 해지도 스토어에서.
const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';
const GOOGLE_SUBSCRIPTIONS_URL =
  'https://play.google.com/store/account/subscriptions?package=com.jalam.dev.app';

interface SubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
}

/** 구독 관리 안내 — 결제한 스토어의 구독 관리 페이지로 보낸다 */
export function SubscriptionManageDialog({ open, onClose }: SubscriptionDialogProps) {
  return (
    <AppDialog open={open} title="구독 관리 · 해지" onClose={onClose} maxWidth="xs">
      <Typography variant="body2" sx={{ color: 'var(--fg-secondary)', mb: 'var(--space-4)' }}>
        일기월장 멤버십은 모바일 앱에서 결제한 스토어에서 관리·해지해요. 해지해도 이미 결제한 기간이
        끝날 때까지는 계속 이용할 수 있어요.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <Button variant="outlined" href={APPLE_SUBSCRIPTIONS_URL} target="_blank" rel="noopener noreferrer">
          App Store 구독 관리
        </Button>
        <Button variant="outlined" href={GOOGLE_SUBSCRIPTIONS_URL} target="_blank" rel="noopener noreferrer">
          Google Play 구독 관리
        </Button>
      </Box>
    </AppDialog>
  );
}

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
  /** 탈퇴 성공 후 로그아웃 처리 */
  onDeleted: () => Promise<void>;
  onOpenSubscription: () => void;
}

/**
 * 회원 탈퇴 — 서버(Cloud Functions deleteMyAccount)가 데이터와 로그인 계정을 지운다.
 * 앱과 같은 함수를 쓴다. 다른 멤버가 있는 공유가계부의 방장이면 서버가 거부한다.
 */
export function DeleteAccountDialog({ open, onClose, onDeleted, onOpenSubscription }: DeleteAccountDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (busy) return;
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const call = httpsCallable(getFunctions(app, 'asia-northeast3'), 'deleteMyAccount', {
        timeout: 300_000,
      });
      await call();
      await onDeleted();
    } catch (e) {
      const code = (e as { code?: string }).code;
      setError(
        code === 'functions/failed-precondition'
          ? '다른 멤버가 있는 공유가계부의 방장이에요. 방장을 넘기거나 공유가계부를 삭제한 뒤 탈퇴해주세요.'
          : '탈퇴 처리 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      title="회원 탈퇴"
      onClose={handleClose}
      maxWidth="xs"
      actions={
        <>
          <Button onClick={handleClose} disabled={busy}>
            취소
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={busy}>
            {busy ? '탈퇴 처리 중…' : '탈퇴하기'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ color: 'var(--fg-secondary)', mb: 'var(--space-3)' }}>
        계정과 연결된 모든 데이터(가계부·직원·백업 등)가 삭제되고 복구할 수 없어요. 앱과 PC에서 모두 같은 계정이
        삭제돼요.
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--c-red-600)', fontWeight: 600, mb: 'var(--space-2)' }}>
        구독 중이라면 탈퇴해도 구독은 자동으로 해지되지 않아요. 결제가 계속되지 않게 먼저 스토어에서 해지해주세요.
      </Typography>
      <Button size="small" onClick={onOpenSubscription} disabled={busy}>
        구독 관리 열기
      </Button>
      {error && (
        <Typography variant="body2" sx={{ color: 'var(--c-red-600)', mt: 'var(--space-3)' }}>
          {error}
        </Typography>
      )}
    </AppDialog>
  );
}
